import React from 'react';
import { TableCell, Tooltip } from '@mui/material';
import { formatCurrency } from 'utils';
import { CalculatedValueDisplay, CheckMarkDisplay } from '../components/common';
import { getDataCellClass } from 'components/tables/common/simpleTableClasses';
import reviewedIcon from 'assets/images/riviewdicon.svg';
import { AuditItem } from '../types';

type CalculationOperation = 'add' | 'subtract';

export interface CalculationDetail {
  name: string;
  value: number;
  operation?: CalculationOperation;
  displayRole?: 'subtotal';
  isPrimary?: boolean;
  sectionLabel?: string;
  isLineItem?: boolean;
  hideDividerAfter?: boolean;
  differenceLabel?: string;
  positiveDifferenceLabel?: string;
  negativeDifferenceLabel?: string;
  hideOperationPrefix?: boolean;
}

type CalculationDetailsResult = { calculations: CalculationDetail[] } | { isNA: true } | { simpleValue: number };

/**
 * Transforms new audit structure from GET_STORE_AUDIT_SUMMARIES to AuditItem format
 */
export const transformAuditToAuditItem = (audit: any): AuditItem => {
  const reportDatetime = audit.audit_date || audit.report_datetime || '';
  const auditSummary = audit.audit_summary || {};
  const revenue = auditSummary.total_revenue ?? auditSummary.revenue ?? audit.revenue ?? 0;
  const posSummary = audit.pos_summary || {};
  const scratcherCount = posSummary.scratcher_count ?? 0;

  const auditItem: any = {
    audit_id: audit.audit_id || '',
    report_datetime: reportDatetime,
    shift_start_date: audit.shift_start_date ?? null,
    shift_start_time: audit.shift_start_time ?? null,
    shift_end_time: audit.shift_end_time ?? null,
    operator_name: audit.operator_name ?? null,
    user_name: audit.user_name || '',
    location_key: audit.location_key ?? null,
    location_name: audit.location_name ?? null,
    location_type: audit.location_type ?? null,
    show_location: Boolean(audit.show_location),
    is_autogenerated: Boolean(
      audit.is_autogenerated ?? audit.isAutogenerated
    ),
    audits: 1,
    opening_count: auditSummary.opening_count ?? audit.opening_count ?? 0,
    opening_pack_count: auditSummary.opening_pack_count ?? audit.opening_pack_count ?? 0,
    newly_activated_count: auditSummary.newly_activated_count ?? audit.newly_activated_count ?? 0,
    newly_activated_pack_count: auditSummary.newly_activated_pack_count ?? audit.newly_activated_pack_count ?? 0,
    closing_count: auditSummary.closing_count ?? audit.closing_count ?? 0,
    closing_pack_count: auditSummary.closing_pack_count ?? audit.closing_pack_count ?? 0,
    moved_to_backstock_count: auditSummary.moved_to_backstock_count ?? audit.moved_to_backstock_count ?? 0,
    moved_to_backstock_ticket_count: auditSummary.moved_to_backstock_ticket_count ?? audit.moved_to_backstock_ticket_count ?? 0,
    total_sold: auditSummary.total_sold ?? audit.total_sold ?? 0,
    revenue: revenue,
    free_scratcher: 0,
    scratcher_payout: 0,
    lotto_payout: 0,
    total_payout: 0,
    scratcher_sold_qty: scratcherCount,
    scratcher_sold_amount: posSummary.scratcher_sales ?? 0,
    lotto_sold_amount: posSummary.lotto_sales ?? 0,
    total_amount: 0,
    pos_reported_payout: posSummary.payout ?? 0,
    pos_data: posSummary ? {
      pos_summary: {
        scratcher_quantity_sold: String(scratcherCount),
        scratcher_sales: String(posSummary.scratcher_sales ?? 0),
        lotto_sales: String(posSummary.lotto_sales ?? 0),
        payout: String(posSummary.payout ?? 0),
      }
    } : undefined,
    audit_summary: auditSummary ? { revenue: revenue } : undefined,
    lotto_summary: audit.lotto_summary ? {
      sales: audit.lotto_summary.sales ?? 0,
      payouts_total: audit.lotto_summary.payouts_total ?? 0,
      scratcher_free_tickets_paid_amount: audit.lotto_summary.scratcher_free_tickets_paid_amount ?? 0,
    } : undefined,
    reconciliation_variance: audit.reconciliation_variance ? {
      scratcher: audit.reconciliation_variance.scratcher ? {
        quantity_variance: audit.reconciliation_variance.scratcher.quantity_variance ?? 0,
        revenue_variance: audit.reconciliation_variance.scratcher.revenue_variance ?? 0,
      } : undefined,
      lotto_sales_variance: audit.reconciliation_variance.lotto_sales_variance ?? 0,
      payout_variance: audit.reconciliation_variance.payout_variance ?? 0,
      total_variance: audit.reconciliation_variance.total_variance ?? 0,
    } : undefined,
  };

  return auditItem;
};

/**
 * Helper function to check if lotto summary is all zero
 */
export const isLottoSummaryAllZero = (lottoSummary: any): boolean => {
  if (!lottoSummary) return true;
  return (
    (lottoSummary.sales ?? 0) === 0 &&
    (lottoSummary.payouts_total ?? 0) === 0 &&
    (lottoSummary.payouts_count ?? 0) === 0 &&
    (lottoSummary.lotto_payouts_amount ?? 0) === 0 &&
    (lottoSummary.lotto_payouts_count ?? 0) === 0 &&
    (lottoSummary.scratcher_payouts_amount ?? 0) === 0 &&
    (lottoSummary.scratcher_payouts_count ?? 0) === 0 &&
    (lottoSummary.scratcher_free_tickets_paid_count ?? 0) === 0 &&
    (lottoSummary.scratcher_free_tickets_paid_amount ?? 0) === 0
  );
};

/**
 * Helper function to check if pos summary is all zero
 */
export const isPosSummaryAllZero = (posSummary: any): boolean => {
  if (!posSummary) return true;
  return (
    (posSummary.scratcher_quantity_sold ?? posSummary.scratcher_count ?? 0) === 0 &&
    (posSummary.scratcher_sales ?? 0) === 0 &&
    (posSummary.lotto_sales ?? 0) === 0 &&
    (posSummary.payout ?? 0) === 0
  );
};

/**
 * Prepares discrepancy details for scratcher counts column
 */
export const prepareScratcherCountsDetails = (
  posValue: number,
  inventoryValue: number,
  variance: number | undefined | null,
  isPosZero: boolean,
  isLottoZero: boolean,
  hasPosType: boolean = true
): CalculationDetailsResult => {
  // If POS is not available, show simple value (audit.total_sold) - no hover, no discrepancy
  if (!hasPosType) {
    return { simpleValue: inventoryValue };
  }

  if (isPosZero && isLottoZero && (variance === 0 || variance === undefined || variance === null)) {
    return { isNA: true };
  }

  if (inventoryValue !== 0 || (variance !== undefined && variance !== null)) {
    return {
      calculations: [
        { name: 'POS Tickets Sold', value: posValue, operation: 'add' as const },
        { name: 'Expected Tickets Sales', value: inventoryValue, operation: 'subtract' as const }
      ]
    };
  }

  return {
    calculations: [
      { name: 'POS Tickets Sold', value: posValue, operation: 'add' as const }
    ]
  };
};

/**
 * Prepares discrepancy details for scratcher sales column
 */
export const prepareScratcherSalesDetails = (
  posValue: number,
  inventoryValue: number,
  variance: number | undefined | null,
  isPosZero: boolean,
  isLottoZero: boolean,
  hasPosType: boolean = true
): CalculationDetailsResult => {
  // If POS is not available, show simple value (audit.total_revenue) - no hover, no discrepancy
  if (!hasPosType) {
    return { simpleValue: inventoryValue };
  }

  if (isPosZero && isLottoZero && (variance === 0 || variance === undefined || variance === null)) {
    return { isNA: true };
  }

  if (inventoryValue !== 0 || (variance !== undefined && variance !== null)) {
    return {
      calculations: [
        { name: 'POS Sales', value: posValue, operation: 'add' as const },
        { name: 'Expected Sales', value: inventoryValue, operation: 'subtract' as const }
      ]
    };
  }

  return {
    calculations: [
      { name: 'POS Sales', value: posValue, operation: 'add' as const }
    ]
  };
};

/**
 * Prepares discrepancy details for lotto sales column
 */
export const prepareLottoSalesDetails = (
  posValue: number,
  lottoValue: number,
  variance: number | undefined | null,
  isPosZero: boolean,
  isLottoZero: boolean,
  hasPosType: boolean = true
): CalculationDetailsResult => {
  // If POS is not available, show simple value (lotto.sales) - no hover, no discrepancy
  if (!hasPosType) {
    return { simpleValue: lottoValue };
  }

  if (isPosZero && isLottoZero && (variance === 0 || variance === undefined || variance === null)) {
    return { isNA: true };
  }

  if (lottoValue !== 0 || (variance !== undefined && variance !== null)) {
    return {
      calculations: [
        { name: 'POS Lotto Sales', value: posValue, operation: 'add' as const },
        { name: 'Lottery System reported', value: lottoValue, operation: 'subtract' as const }
      ]
    };
  }

  return {
    calculations: [
      { name: 'POS Lotto Sales', value: posValue, operation: 'add' as const }
    ]
  };
};

/**
 * Prepares discrepancy details for payout column
 */
export const preparePayoutDetails = (
  lottoPayoutsTotal: number,
  freeTicketsAmount: number,
  posPayout: number,
  variance: number | undefined | null,
  isPosZero: boolean,
  isLottoZero: boolean,
  hasPosType: boolean = true
): CalculationDetailsResult => {
  // If POS is not available, show simple value (lotto.payouts_total + lotto.scratcher_free_tickets_paid_amount) - no hover, no discrepancy
  if (!hasPosType) {
    const totalPayout = lottoPayoutsTotal + freeTicketsAmount;
    return { simpleValue: totalPayout };
  }

  // Only show N/A if all values are zero AND no variance exists
  if (lottoPayoutsTotal === 0 && freeTicketsAmount === 0 && posPayout === 0 &&
    (variance === 0 || variance === undefined || variance === null) && isPosZero && isLottoZero) {
    return { isNA: true };
  }

  // Always show full calculation breakdown, even if lotto payouts are zero
  // This ensures discrepancy is visible when variance exists
  const lottoTotal = lottoPayoutsTotal + freeTicketsAmount;

  return {
    calculations: [
      {
        name: 'Payouts reported',
        value: lottoPayoutsTotal,
        operation: 'add' as const,
        sectionLabel: 'STATE LOTTERY',
        isLineItem: true,
        hideDividerAfter: true,
        hideOperationPrefix: true,
      },
      { name: 'Free tickets paid', value: freeTicketsAmount, operation: 'add' as const },
      {
        name: 'Lotto Total',
        value: lottoTotal,
        displayRole: 'subtotal' as const,
        isPrimary: true,
        differenceLabel: 'Lotto Total - POS Reported',
        positiveDifferenceLabel: 'POS reported less payout (Profit):',
        negativeDifferenceLabel: 'POS reported more payout (Loss):',
      },
      { name: 'POS reported payout', value: posPayout, operation: 'subtract' as const, sectionLabel: 'POS' }
    ]
  };
};

/**
 * Component for displaying total discrepancy
 */
export const TotalDiscrepancyDisplay = ({ totalDiscrepancy }: { totalDiscrepancy: number | { isNA: true } }) => {
  if (typeof totalDiscrepancy === 'object' && 'isNA' in totalDiscrepancy) {
    return <span style={{ color: '#666' }} title="No data available">-</span>;
  }

  const isPositive = totalDiscrepancy > 0;
  const displayValue = `${totalDiscrepancy > 0 ? '+' : ''}${formatCurrency(totalDiscrepancy)}`;
  const title = totalDiscrepancy === 0 ? "All audits matched" : `Total discrepancy: ${displayValue}`;

  if (isPositive) {
    return <span className="discrepancy--positive" title={title}>{displayValue}</span>;
  }

  return <CheckMarkDisplay value={totalDiscrepancy} title={title} />;
};

/**
 * Helper to render a CalculatedValueDisplay cell
 */
const renderCalculatedCell = (
  details: any,
  columnIndex: number,
  borderColumns: number[],
  isChildRow: boolean = false,
  formatFunction?: (value: number) => string,
  additionalStyle?: React.CSSProperties,
  rowIdx: number = 0,
  totalRows: number = 2,
  isInProgressRow: boolean = false,
  showZeroDiscrepancyPopover: boolean = false
) => {
  const className = isChildRow
    ? `child-row-data-processor__cell ${getDataCellClass(columnIndex, rowIdx, totalRows, borderColumns)}`
    : getDataCellClass(columnIndex, rowIdx, totalRows, borderColumns);
  const style = isChildRow
    ? { opacity: 1, ...additionalStyle }
    : additionalStyle;

  if (isInProgressRow && !('simpleValue' in details)) {
    return (
      <TableCell key={columnIndex} className={className} style={style}>
        {renderInProgressPlaceholder()}
      </TableCell>
    );
  }

  if ('isNA' in details) {
    return (
      <TableCell key={columnIndex} className={className} style={style}>
        <CalculatedValueDisplay isNA={details.isNA} style={style} />
      </TableCell>
    );
  }

  // If simpleValue exists, show it directly without hover (no discrepancy display)
  if ('simpleValue' in details) {
    const displayValue = formatFunction
      ? formatFunction(details.simpleValue)
      : formatCurrency(details.simpleValue);
    return (
      <TableCell key={columnIndex} className={className} style={style}>
        <span className={isInProgressRow ? 'audit-table__wip-value' : ''}>{displayValue}</span>
      </TableCell>
    );
  }

  return (
    <TableCell key={columnIndex} className={className} style={style}>
      <CalculatedValueDisplay
        calculations={details.calculations}
        formatFunction={formatFunction}
        style={style}
        showZeroDiscrepancyPopover={showZeroDiscrepancyPopover}
      />
    </TableCell>
  );
};

/**
 * Helper to extract values from item for child rows
 */
const extractChildRowValues = (item: AuditItem) => {
  const posLottoSales = Number(item?.pos_data?.pos_summary?.lotto_sales) ?? 0;
  const lottoSystemSales = Number(item?.lotto_summary?.sales) ?? 0;
  const lottoPayoutsTotal = Number(item?.lotto_summary?.payouts_total) ?? 0;
  const freeTicketsPaid = Number(item?.lotto_summary?.scratcher_free_tickets_paid_amount) ?? 0;
  const posPayout = Number(item?.pos_data?.pos_summary?.payout) ?? 0;
  const posScratcherQuantity = Number(item?.pos_data?.pos_summary?.scratcher_quantity_sold) ?? 0;
  const totalSold = Number(item?.total_sold) ?? 0;
  const posScratcherSales = Number(item?.pos_data?.pos_summary?.scratcher_sales) ?? 0;
  const revenue = Number(item?.revenue) ?? 0;

  const isPosZero = posScratcherQuantity === 0 && posScratcherSales === 0 && posLottoSales === 0 && posPayout === 0;
  const isLottoZero = lottoSystemSales === 0 && lottoPayoutsTotal === 0 && freeTicketsPaid === 0;

  return {
    posLottoSales,
    lottoSystemSales,
    lottoPayoutsTotal,
    freeTicketsPaid,
    posPayout,
    posScratcherQuantity,
    totalSold,
    posScratcherSales,
    revenue,
    isPosZero,
    isLottoZero
  };
};

/**
 * Column configuration types
 */
type ColumnType =
  | 'scratcherCounts'
  | 'expectedSales'
  | 'expectedSalesCombined'
  | 'lottoSystemSales'
  | 'posLottoSales'
  | 'lottoSalesVariance'
  | 'scratcherSales'
  | 'scratcherSalesCombined'
  | 'scratcherVarianceQty'
  | 'scratcherVarianceRevenue'
  | 'scratcherVarianceCombined'
  | 'lottoPayouts'
  | 'freeTicketPayouts'
  | 'posPayouts'
  | 'payoutVariance'
  | 'scratcherFinal';

interface ColumnConfig {
  type: ColumnType;
  columnIndex: number;
}

const offsetColumnConfig = (config: ColumnConfig[], offset: number): ColumnConfig[] => {
  if (!offset) {
    return config;
  }

  return config.map((column) => ({
    ...column,
    columnIndex: column.columnIndex + offset,
  }));
};

const COLUMN_CONFIGS = {
  bothConnected: [
    { type: 'expectedSalesCombined' as const, columnIndex: 5 },
    { type: 'scratcherSalesCombined' as const, columnIndex: 6 },
    { type: 'scratcherVarianceCombined' as const, columnIndex: 7 },
    { type: 'lottoSystemSales' as const, columnIndex: 8 },
    { type: 'posLottoSales' as const, columnIndex: 9 },
    { type: 'lottoSalesVariance' as const, columnIndex: 10 },
    { type: 'lottoPayouts' as const, columnIndex: 11 },
    { type: 'freeTicketPayouts' as const, columnIndex: 12 },
    { type: 'posPayouts' as const, columnIndex: 13 },
    { type: 'payoutVariance' as const, columnIndex: 14 }
  ],
  bothNotConnected: [
    { type: 'scratcherCounts' as const, columnIndex: 7 },
    { type: 'scratcherSales' as const, columnIndex: 8 }
  ],
  singleConnected: [
    { type: 'scratcherCounts' as const, columnIndex: 7 },
    { type: 'scratcherSales' as const, columnIndex: 8 },
    { type: 'lottoSystemSales' as const, columnIndex: 9 },
    { type: 'lottoPayouts' as const, columnIndex: 10 }
  ],
  posConnectedOnly: [
    { type: 'expectedSalesCombined' as const, columnIndex: 5 },
    { type: 'scratcherSalesCombined' as const, columnIndex: 6 },
    { type: 'scratcherVarianceCombined' as const, columnIndex: 7 },
    { type: 'lottoSystemSales' as const, columnIndex: 8 },
    { type: 'posLottoSales' as const, columnIndex: 9 },
    { type: 'lottoSalesVariance' as const, columnIndex: 10 },
    { type: 'lottoPayouts' as const, columnIndex: 11 },
    { type: 'freeTicketPayouts' as const, columnIndex: 12 },
    { type: 'posPayouts' as const, columnIndex: 13 },
    { type: 'payoutVariance' as const, columnIndex: 14 }
  ]
};

const LOTTO_AND_PAYOUT_COLUMN_TYPES: ColumnType[] = [
  'lottoSystemSales',
  'posLottoSales',
  'lottoSalesVariance',
  'lottoPayouts',
  'freeTicketPayouts',
  'posPayouts',
  'payoutVariance',
];

const hideLottoAndPayoutColumns = (config: ColumnConfig[]): ColumnConfig[] => {
  return config.filter((column) => !LOTTO_AND_PAYOUT_COLUMN_TYPES.includes(column.type));
};

const ScratcherVarianceDisplay = ({
  value,
  formatValue,
  title,
}: {
  value?: number | null;
  formatValue: (value: number) => string;
  title: string;
}) => {
  const variance = Number(value ?? 0);

  if (variance === 0) {
    return <CheckMarkDisplay value={0} title={`${title} matched`} />;
  }

  const className = variance > 0
    ? 'audit-table__scratcher-variance audit-table__scratcher-variance--positive'
    : 'audit-table__scratcher-variance audit-table__scratcher-variance--negative';

  return (
    <span className={className} title={title}>
      {formatValue(variance)}
    </span>
  );
};

/**
 * Renders columns based on configuration
 */
const withVarianceCellBackground = (className: string) => `${className} audit-table__variance-cell-bg`;
const withVarianceDollarRightBorder = (className: string) => `${className} audit-table__variance-dollar-right-border`;
const VARIANCE_COLUMN_STYLE: React.CSSProperties = {
  textAlign: 'right',
};

const renderInProgressPlaceholder = () => (
  <span className="audit-table__wip-value">-</span>
);

const renderInProgressVarianceCell = (
  columnIndex: number,
  borderColumns: number[],
  isChildRow: boolean,
  rowIdx: number,
  totalRows: number,
  classNameDecorator: (className: string) => string = (className) => className
) => {
  const baseClassName = isChildRow
    ? `child-row-data-processor__cell ${getDataCellClass(columnIndex, rowIdx, totalRows, borderColumns)}`
    : getDataCellClass(columnIndex, rowIdx, totalRows, borderColumns);

  return (
    <TableCell key={columnIndex} className={classNameDecorator(baseClassName)} style={VARIANCE_COLUMN_STYLE}>
      {renderInProgressPlaceholder()}
    </TableCell>
  );
};

const formatSignedTicketVariance = (value: number) => {
  if (value > 0) {
    return `+${value.toLocaleString()}`;
  }

  if (value < 0) {
    return `-${Math.abs(value).toLocaleString()}`;
  }

  return null;
};

const renderAmountWithQuantity = (
  amount: React.ReactNode,
  quantity: React.ReactNode,
  quantityLabel = 'tickets',
  className = ''
) => (
  <div className={`audit-table__amount-with-quantity ${className}`.trim()}>
    <span className="audit-table__amount-with-quantity-value">{amount}</span>
    {quantity != null && (
      <span className="audit-table__amount-with-quantity-subscript">
        {quantity} {quantityLabel}
      </span>
    )}
  </div>
);

const renderColumns = (
  config: ColumnConfig[],
  details: {
    scratcherCountsDetails: any;
    scratcherSalesDetails: any;
    scratcherQuantityVariance?: number | null;
    scratcherRevenueVariance?: number | null;
    expectedSalesValue?: number;
    expectedQuantityValue?: number;
    posScratcherSalesValue?: number;
    posScratcherQuantityValue?: number;
    lottoSystemSalesValue?: number;
    posLottoSalesValue?: number;
    lottoSalesVariance?: number | null;
    lottoPayoutsValue?: number;
    freeTicketPayoutsValue?: number;
    posPayoutsValue?: number;
    payoutVariance?: number | null;
    lottoSalesDetails?: any;
    payoutDetails?: any;
    totalDiscrepancy?: any;
    lottoPayoutsTotal?: number;
  },
  borderColumns: number[],
  isChildRow: boolean = false,
  isLottoOnly: boolean = false,
  widenScratcherSales: boolean = false,
  enableMatchedPopoverColumns: ColumnType[] = [],
  rowIdx: number = 0,
  totalRows: number = 2,
  isInProgressRow: boolean = false
) => {
  return config.map((col) => {
    const showMatchedPopover = enableMatchedPopoverColumns.includes(col.type);
    switch (col.type) {
      case 'scratcherCounts':
        // For lotto-only case, ensure equal width for Scratcher Summary subcolumns
        const scratcherCountsStyle = VARIANCE_COLUMN_STYLE;
        return renderCalculatedCell(
          details.scratcherCountsDetails,
          col.columnIndex,
          borderColumns,
          isChildRow,
          (value) => value.toString(),
          scratcherCountsStyle,
          rowIdx,
          totalRows,
          isInProgressRow,
          showMatchedPopover
        );
      case 'lottoSystemSales':
        const lottoSalesClassName = isChildRow
          ? `child-row-data-processor__cell ${getDataCellClass(col.columnIndex, rowIdx, totalRows, borderColumns)}`
          : getDataCellClass(col.columnIndex, rowIdx, totalRows, borderColumns);
        return (
          <TableCell key={col.columnIndex} className={lottoSalesClassName} style={VARIANCE_COLUMN_STYLE}>
            {isInProgressRow
              ? renderInProgressPlaceholder()
              : <span>{formatCurrency(details.lottoSystemSalesValue ?? 0)}</span>}
          </TableCell>
        );
      case 'posLottoSales':
        return (
          <TableCell
            key={col.columnIndex}
            className={
              isChildRow
                ? `child-row-data-processor__cell ${getDataCellClass(col.columnIndex, rowIdx, totalRows, borderColumns)}`
                : getDataCellClass(col.columnIndex, rowIdx, totalRows, borderColumns)
            }
            style={VARIANCE_COLUMN_STYLE}
          >
            <span className={isInProgressRow ? 'audit-table__wip-value' : ''}>
              {formatCurrency(details.posLottoSalesValue ?? 0)}
            </span>
          </TableCell>
        );
      case 'lottoSalesVariance':
        if (isInProgressRow) {
          return renderInProgressVarianceCell(
            col.columnIndex,
            borderColumns,
            isChildRow,
            rowIdx,
            totalRows,
            (className) => withVarianceDollarRightBorder(withVarianceCellBackground(className))
          );
        }

        return (
          <TableCell
            key={col.columnIndex}
            className={withVarianceDollarRightBorder(withVarianceCellBackground(
              isChildRow
                ? `child-row-data-processor__cell ${getDataCellClass(col.columnIndex, rowIdx, totalRows, borderColumns)}`
                : getDataCellClass(col.columnIndex, rowIdx, totalRows, borderColumns)
            ))}
            style={VARIANCE_COLUMN_STYLE}
          >
            <ScratcherVarianceDisplay
              value={details.lottoSalesVariance}
              formatValue={formatCurrency}
              title="Lotto sales variance"
            />
          </TableCell>
        );
      case 'expectedSalesCombined':
        return (
          <TableCell
            key={col.columnIndex}
            className={
              isChildRow
                ? `child-row-data-processor__cell ${getDataCellClass(col.columnIndex, rowIdx, totalRows, borderColumns)}`
                : getDataCellClass(col.columnIndex, rowIdx, totalRows, borderColumns)
            }
            style={VARIANCE_COLUMN_STYLE}
          >
            {renderAmountWithQuantity(
              <span className={isInProgressRow ? 'audit-table__wip-value' : ''}>
                {formatCurrency(details.expectedSalesValue ?? 0)}
              </span>,
              Number(details.expectedQuantityValue ?? 0).toLocaleString()
            )}
          </TableCell>
        );
      case 'scratcherSalesCombined':
        return (
          <TableCell
            key={col.columnIndex}
            className={
              isChildRow
                ? `child-row-data-processor__cell ${getDataCellClass(col.columnIndex, rowIdx, totalRows, borderColumns)}`
                : getDataCellClass(col.columnIndex, rowIdx, totalRows, borderColumns)
            }
            style={VARIANCE_COLUMN_STYLE}
          >
            {renderAmountWithQuantity(
              <span className={isInProgressRow ? 'audit-table__wip-value' : ''}>
                {formatCurrency(details.posScratcherSalesValue ?? 0)}
              </span>,
              Number(details.posScratcherQuantityValue ?? 0).toLocaleString()
            )}
          </TableCell>
        );
      case 'scratcherVarianceCombined':
        if (isInProgressRow) {
          return renderInProgressVarianceCell(
            col.columnIndex,
            borderColumns,
            isChildRow,
            rowIdx,
            totalRows,
            (className) => withVarianceDollarRightBorder(withVarianceCellBackground(className))
          );
        }

        return (
          <TableCell
            key={col.columnIndex}
            className={withVarianceDollarRightBorder(withVarianceCellBackground(
              isChildRow
                ? `child-row-data-processor__cell ${getDataCellClass(col.columnIndex, rowIdx, totalRows, borderColumns)}`
                : getDataCellClass(col.columnIndex, rowIdx, totalRows, borderColumns)
            ))}
            style={VARIANCE_COLUMN_STYLE}
          >
            {renderAmountWithQuantity(
              <ScratcherVarianceDisplay
                value={details.scratcherRevenueVariance}
                formatValue={formatCurrency}
                title="Scratcher dollar variance"
              />,
              formatSignedTicketVariance(Number(details.scratcherQuantityVariance ?? 0)),
              'tickets',
              Number(details.scratcherRevenueVariance ?? 0) > 0
                ? 'audit-table__amount-with-quantity--positive'
                : Number(details.scratcherRevenueVariance ?? 0) < 0
                  ? 'audit-table__amount-with-quantity--negative'
                  : 'audit-table__amount-with-quantity--center'
            )}
          </TableCell>
        );
      case 'scratcherSales':
        const scratcherSalesStyle = VARIANCE_COLUMN_STYLE;
        return renderCalculatedCell(
          details.scratcherSalesDetails,
          col.columnIndex,
          borderColumns,
          isChildRow,
          undefined,
          scratcherSalesStyle,
          rowIdx,
          totalRows,
          isInProgressRow,
          showMatchedPopover
        );
      case 'expectedSales':
        return (
          <TableCell
            key={col.columnIndex}
            className={
              isChildRow
                ? `child-row-data-processor__cell ${getDataCellClass(col.columnIndex, rowIdx, totalRows, borderColumns)}`
                : getDataCellClass(col.columnIndex, rowIdx, totalRows, borderColumns)
            }
            style={VARIANCE_COLUMN_STYLE}
          >
            <span className={isInProgressRow ? 'audit-table__wip-value' : ''}>
              {formatCurrency(details.expectedSalesValue ?? 0)}
            </span>
          </TableCell>
        );
      case 'scratcherVarianceQty':
        if (isInProgressRow) {
          return renderInProgressVarianceCell(
            col.columnIndex,
            borderColumns,
            isChildRow,
            rowIdx,
            totalRows,
            withVarianceCellBackground
          );
        }

        return (
          <TableCell
            key={col.columnIndex}
            className={withVarianceCellBackground(
              isChildRow
                ? `child-row-data-processor__cell ${getDataCellClass(col.columnIndex, rowIdx, totalRows, borderColumns)}`
                : getDataCellClass(col.columnIndex, rowIdx, totalRows, borderColumns)
            )}
            style={VARIANCE_COLUMN_STYLE}
          >
            <ScratcherVarianceDisplay
              value={details.scratcherQuantityVariance}
              formatValue={(value) => value.toLocaleString()}
              title="Scratcher quantity variance"
            />
          </TableCell>
        );
      case 'scratcherVarianceRevenue':
        if (isInProgressRow) {
          return renderInProgressVarianceCell(
            col.columnIndex,
            borderColumns,
            isChildRow,
            rowIdx,
            totalRows,
            (className) => withVarianceDollarRightBorder(withVarianceCellBackground(className))
          );
        }

        return (
          <TableCell
            key={col.columnIndex}
            className={withVarianceDollarRightBorder(withVarianceCellBackground(
              isChildRow
                ? `child-row-data-processor__cell ${getDataCellClass(col.columnIndex, rowIdx, totalRows, borderColumns)}`
                : getDataCellClass(col.columnIndex, rowIdx, totalRows, borderColumns)
            ))}
            style={VARIANCE_COLUMN_STYLE}
          >
            <ScratcherVarianceDisplay
              value={details.scratcherRevenueVariance}
              formatValue={formatCurrency}
              title="Scratcher dollar variance"
            />
          </TableCell>
        );
      case 'scratcherFinal':
        // Show scratcher revenue_variance for Scratcher Final column
        const finalClassName = isChildRow
          ? `child-row-data-processor__cell ${getDataCellClass(col.columnIndex, rowIdx, totalRows, borderColumns)}`
          : getDataCellClass(col.columnIndex, rowIdx, totalRows, borderColumns);
        const scratcherFinalValue = details.scratcherRevenueVariance;

        if (isInProgressRow) {
          return (
            <TableCell key={col.columnIndex} className={finalClassName} style={VARIANCE_COLUMN_STYLE}>
              {renderInProgressPlaceholder()}
            </TableCell>
          );
        }

        return (
          <TableCell key={col.columnIndex} className={finalClassName} style={VARIANCE_COLUMN_STYLE}>
            {scratcherFinalValue !== undefined && scratcherFinalValue !== null ? (
              <TotalDiscrepancyDisplay totalDiscrepancy={scratcherFinalValue} />
            ) : (
              <span style={{ color: '#666' }} title="No data available">-</span>
            )}
          </TableCell>
        );
      case 'lottoPayouts':
        const payoutsClassName = isChildRow
          ? `child-row-data-processor__cell ${getDataCellClass(col.columnIndex, rowIdx, totalRows, borderColumns)}`
          : getDataCellClass(col.columnIndex, rowIdx, totalRows, borderColumns);
        return (
          <TableCell key={col.columnIndex} className={payoutsClassName} style={VARIANCE_COLUMN_STYLE}>
            {isInProgressRow
              ? renderInProgressPlaceholder()
              : <span>{formatCurrency(details.lottoPayoutsValue ?? details.lottoPayoutsTotal ?? 0)}</span>}
          </TableCell>
        );
      case 'freeTicketPayouts':
      case 'posPayouts':
        if (isInProgressRow && col.type === 'freeTicketPayouts') {
          return (
            <TableCell
              key={col.columnIndex}
              className={
                isChildRow
                  ? `child-row-data-processor__cell ${getDataCellClass(col.columnIndex, rowIdx, totalRows, borderColumns)}`
                  : getDataCellClass(col.columnIndex, rowIdx, totalRows, borderColumns)
              }
              style={VARIANCE_COLUMN_STYLE}
            >
              {renderInProgressPlaceholder()}
            </TableCell>
          );
        }
        const payoutValue = col.type === 'freeTicketPayouts'
          ? details.freeTicketPayoutsValue
          : details.posPayoutsValue;
        return (
          <TableCell
            key={col.columnIndex}
            className={
              isChildRow
                ? `child-row-data-processor__cell ${getDataCellClass(col.columnIndex, rowIdx, totalRows, borderColumns)}`
                : getDataCellClass(col.columnIndex, rowIdx, totalRows, borderColumns)
            }
            style={VARIANCE_COLUMN_STYLE}
          >
            <span className={isInProgressRow ? 'audit-table__wip-value' : ''}>
              {formatCurrency(payoutValue ?? 0)}
            </span>
          </TableCell>
        );
      case 'payoutVariance':
        if (isInProgressRow) {
          return renderInProgressVarianceCell(
            col.columnIndex,
            borderColumns,
            isChildRow,
            rowIdx,
            totalRows,
            (className) => withVarianceDollarRightBorder(withVarianceCellBackground(className))
          );
        }

        return (
          <TableCell
            key={col.columnIndex}
            className={withVarianceDollarRightBorder(withVarianceCellBackground(
              isChildRow
                ? `child-row-data-processor__cell ${getDataCellClass(col.columnIndex, rowIdx, totalRows, borderColumns)}`
                : getDataCellClass(col.columnIndex, rowIdx, totalRows, borderColumns)
            ))}
            style={VARIANCE_COLUMN_STYLE}
          >
            <ScratcherVarianceDisplay
              value={details.payoutVariance}
              formatValue={formatCurrency}
              title="Payout variance"
            />
          </TableCell>
        );
      default:
        return null;
    }
  });
};

/**
 * Conditional columns component for parent rows
 */
export const ConditionalColumns = ({
  isBothConnected,
  isBothNotConnected,
  isLottoConnectedOnly,
  isPOSConnectedOnly,
  scratcherCountsDetails,
  lottoSalesDetails,
  scratcherSalesDetails,
  payoutDetails,
  totalDiscrepancy,
  borderColumns,
  lottoPayoutsTotal,
  freeTicketPayoutsTotal,
  posPayout,
  lottoSales,
  posLottoSales,
  lottoSalesVariance,
  scratcherQuantityVariance,
  scratcherRevenueVariance,
  payoutVariance,
  hasPosType,
  isInProgressRow = false,
  scratcherCountDisplayValue = 0,
  scratcherSalesDisplayValue = 0,
  expectedSalesDisplayValue = 0,
  expectedQuantityDisplayValue = scratcherCountDisplayValue,
  posScratcherQuantityDisplayValue = scratcherCountDisplayValue,
  payoutDisplayValue = 0,
  columnOffset = 0,
  hideLottoAndPayout = false,
  rowIdx,
  totalRows
}: {
  isBothConnected: boolean;
  isBothNotConnected: boolean;
  isLottoConnectedOnly: boolean;
  isPOSConnectedOnly: boolean;
  scratcherCountsDetails: any;
  lottoSalesDetails: any;
  scratcherSalesDetails: any;
  payoutDetails: any;
  totalDiscrepancy: any;
  borderColumns: any;
  lottoPayoutsTotal?: number;
  freeTicketPayoutsTotal?: number;
  posPayout?: number;
  lottoSales?: number;
  posLottoSales?: number;
  lottoSalesVariance?: number;
  scratcherQuantityVariance?: number;
  scratcherRevenueVariance?: number;
  payoutVariance?: number;
  hasPosType?: boolean;
  isInProgressRow?: boolean;
  scratcherCountDisplayValue?: number;
  scratcherSalesDisplayValue?: number;
  expectedSalesDisplayValue?: number;
  expectedQuantityDisplayValue?: number;
  posScratcherQuantityDisplayValue?: number;
  payoutDisplayValue?: number;
  columnOffset?: number;
  hideLottoAndPayout?: boolean;
  rowIdx: number;
  totalRows: number;
}) => {
  const filterColumns = hideLottoAndPayout ? hideLottoAndPayoutColumns : (config: ColumnConfig[]) => config;
  const bothConnectedConfig = filterColumns(offsetColumnConfig(COLUMN_CONFIGS.bothConnected, columnOffset));
  const bothNotConnectedConfig = filterColumns(offsetColumnConfig(COLUMN_CONFIGS.bothNotConnected, columnOffset));
  const singleConnectedConfig = filterColumns(offsetColumnConfig(COLUMN_CONFIGS.singleConnected, columnOffset));
  const posConnectedOnlyConfig = filterColumns(offsetColumnConfig(COLUMN_CONFIGS.posConnectedOnly, columnOffset));

  if (isInProgressRow) {
    if (isBothConnected) {
      return <>{renderColumns(bothConnectedConfig, {
        scratcherCountsDetails: { simpleValue: scratcherCountDisplayValue },
        lottoSalesDetails: { simpleValue: lottoSales ?? 0 },
        scratcherSalesDetails: { simpleValue: scratcherSalesDisplayValue },
        scratcherQuantityVariance: 0,
        scratcherRevenueVariance: 0,
        expectedSalesValue: expectedSalesDisplayValue,
        expectedQuantityValue: expectedQuantityDisplayValue,
        posScratcherSalesValue: scratcherSalesDisplayValue,
        posScratcherQuantityValue: posScratcherQuantityDisplayValue,
        lottoSystemSalesValue: lottoSales ?? 0,
        posLottoSalesValue: posLottoSales ?? 0,
        lottoSalesVariance: 0,
        lottoPayoutsValue: payoutDisplayValue,
        freeTicketPayoutsValue: 0,
        posPayoutsValue: payoutDisplayValue,
        payoutVariance: 0,
        payoutDetails: { simpleValue: payoutDisplayValue },
        totalDiscrepancy
      }, borderColumns, false, false, true, [], rowIdx, totalRows, true)}</>;
    }

    if (isBothNotConnected) {
      return <>{renderColumns(bothNotConnectedConfig, {
        scratcherCountsDetails: { isNA: true },
        scratcherSalesDetails: { isNA: true },
        lottoSystemSalesValue: lottoSales ?? 0,
        payoutDetails: { isNA: true },
        totalDiscrepancy
      }, borderColumns, false, false, false, [], rowIdx, totalRows, true)}</>;
    }

    if (isLottoConnectedOnly) {
      return <>{renderColumns(singleConnectedConfig, {
        scratcherCountsDetails: { isNA: true },
        lottoSalesDetails: { simpleValue: lottoSales ?? 0 },
        scratcherSalesDetails: { isNA: true },
        payoutDetails: { isNA: true },
        totalDiscrepancy,
        lottoPayoutsValue: payoutDisplayValue
      }, borderColumns, false, true, isPOSConnectedOnly, [], rowIdx, totalRows, true)}</>;
    }

    if (isPOSConnectedOnly) {
      return <>{renderColumns(posConnectedOnlyConfig, {
        scratcherCountsDetails: { simpleValue: scratcherCountDisplayValue },
        lottoSalesDetails: { simpleValue: lottoSales ?? 0 },
        scratcherSalesDetails: { simpleValue: scratcherSalesDisplayValue },
        scratcherQuantityVariance: 0,
        scratcherRevenueVariance: 0,
        expectedSalesValue: expectedSalesDisplayValue,
        expectedQuantityValue: expectedQuantityDisplayValue,
        posScratcherSalesValue: scratcherSalesDisplayValue,
        posScratcherQuantityValue: posScratcherQuantityDisplayValue,
        lottoSystemSalesValue: lottoSales ?? 0,
        posLottoSalesValue: posLottoSales ?? 0,
        lottoSalesVariance: 0,
        lottoPayoutsValue: 0,
        freeTicketPayoutsValue: 0,
        posPayoutsValue: payoutDisplayValue,
        payoutVariance: 0,
        payoutDetails: { simpleValue: payoutDisplayValue },
        totalDiscrepancy,
        lottoPayoutsTotal: payoutDisplayValue
      }, borderColumns, false, false, true, [], rowIdx, totalRows, true)}</>;
    }
  }

  if (isBothConnected) {
    return <>{renderColumns(bothConnectedConfig, {
      scratcherCountsDetails: { simpleValue: scratcherCountDisplayValue },
      lottoSalesDetails,
      scratcherSalesDetails: { simpleValue: scratcherSalesDisplayValue },
      scratcherQuantityVariance,
      scratcherRevenueVariance,
      expectedSalesValue: expectedSalesDisplayValue,
      expectedQuantityValue: expectedQuantityDisplayValue,
      posScratcherSalesValue: scratcherSalesDisplayValue,
      posScratcherQuantityValue: posScratcherQuantityDisplayValue,
      lottoSystemSalesValue: lottoSales ?? 0,
      posLottoSalesValue: posLottoSales ?? 0,
      lottoSalesVariance,
      lottoPayoutsValue: lottoPayoutsTotal ?? 0,
      freeTicketPayoutsValue: freeTicketPayoutsTotal ?? 0,
      posPayoutsValue: posPayout ?? 0,
      payoutVariance,
      payoutDetails,
      totalDiscrepancy
    }, borderColumns, false, false, true, [], rowIdx, totalRows, false)}</>;
  }

  if (isBothNotConnected) {
    return <>{renderColumns(bothNotConnectedConfig, {
      scratcherCountsDetails,
      scratcherSalesDetails,
      lottoSalesDetails,
      payoutDetails,
      totalDiscrepancy
    }, borderColumns, false, false, false, [], rowIdx, totalRows, false)}</>;
  }

  if (isLottoConnectedOnly) {
    return <>{renderColumns(singleConnectedConfig, {
      scratcherCountsDetails,
      lottoSystemSalesValue: lottoSales ?? 0,
      scratcherSalesDetails,
      payoutDetails,
      totalDiscrepancy,
      lottoPayoutsValue: lottoPayoutsTotal ?? 0
    }, borderColumns, false, true, false, [], rowIdx, totalRows, false)}</>;
  }

  if (isPOSConnectedOnly) {
    return <>{renderColumns(posConnectedOnlyConfig, {
      scratcherCountsDetails: { simpleValue: scratcherCountDisplayValue },
      lottoSalesDetails: { simpleValue: posLottoSales ?? 0 },
      scratcherSalesDetails: { simpleValue: scratcherSalesDisplayValue },
      scratcherQuantityVariance,
      scratcherRevenueVariance,
      expectedSalesValue: expectedSalesDisplayValue,
      expectedQuantityValue: expectedQuantityDisplayValue,
      posScratcherSalesValue: scratcherSalesDisplayValue,
      posScratcherQuantityValue: posScratcherQuantityDisplayValue,
      lottoSystemSalesValue: lottoSales ?? 0,
      posLottoSalesValue: posLottoSales ?? 0,
      lottoSalesVariance,
      lottoPayoutsValue: lottoPayoutsTotal ?? 0,
      freeTicketPayoutsValue: freeTicketPayoutsTotal ?? 0,
      posPayoutsValue: posPayout ?? 0,
      payoutVariance,
      payoutDetails,
      totalDiscrepancy,
    }, borderColumns, false, false, true, [], rowIdx, totalRows, false)}</>;
  }

  return null;
};

/**
 * Renders columns for child rows with simple values for Lotto Sales and Payout
 */
const renderChildRowColumns = (
  config: ColumnConfig[],
  details: {
    scratcherCountsDetails: any;
    scratcherSalesDetails: any;
    scratcherQuantityVariance?: number | null;
    scratcherRevenueVariance?: number | null;
    expectedSalesValue?: number;
    expectedQuantityValue?: number;
    posScratcherSalesValue?: number;
    posScratcherQuantityValue?: number;
    lottoSystemSalesValue?: number;
    posLottoSalesValue?: number;
    lottoSalesVariance?: number | null;
    lottoPayoutsValue?: number;
    freeTicketPayoutsValue?: number;
    posPayoutsValue?: number;
    payoutVariance?: number | null;
    totalDiscrepancy: any;
  },
  item: AuditItem,
  borderColumns: number[],
  hasPosType: boolean = true,
  isPOSConnectedOnly: boolean = false,
  isLottoConnectedOnly: boolean = false,
  rowIdx: number = 0,
  totalRows: number = 2,
  blankColumnTypes: ColumnType[] = []
) => {
  return config.map((col) => {
    const className = `child-row-data-processor__cell ${getDataCellClass(col.columnIndex, rowIdx, totalRows, borderColumns)}`;
    const style = { opacity: 1 };

    if (blankColumnTypes.includes(col.type)) {
      return (
        <TableCell key={col.columnIndex} className={className} style={style} />
      );
    }

    switch (col.type) {
      case 'scratcherCounts':
        return renderCalculatedCell(
          details.scratcherCountsDetails,
          col.columnIndex,
          borderColumns,
          true,
          (value) => value.toString(),
          VARIANCE_COLUMN_STYLE,
          rowIdx,
          totalRows
        );
      case 'lottoSystemSales':
        return (
          <TableCell key={col.columnIndex} className={className} style={{ ...style, ...VARIANCE_COLUMN_STYLE }}>
            {formatCurrency(details.lottoSystemSalesValue ?? 0)}
          </TableCell>
        );
      case 'posLottoSales':
        return (
          <TableCell key={col.columnIndex} className={className} style={{ ...style, ...VARIANCE_COLUMN_STYLE }}>
            {formatCurrency(details.posLottoSalesValue ?? 0)}
          </TableCell>
        );
      case 'lottoSalesVariance':
        return (
          <TableCell key={col.columnIndex} className={withVarianceDollarRightBorder(withVarianceCellBackground(className))} style={{ ...style, ...VARIANCE_COLUMN_STYLE }}>
            <ScratcherVarianceDisplay
              value={details.lottoSalesVariance}
              formatValue={formatCurrency}
              title="Lotto sales variance"
            />
          </TableCell>
        );
      case 'scratcherSales':
        return renderCalculatedCell(details.scratcherSalesDetails, col.columnIndex, borderColumns, true, undefined, VARIANCE_COLUMN_STYLE, rowIdx, totalRows);
      case 'expectedSalesCombined':
        return (
          <TableCell key={col.columnIndex} className={className} style={{ ...style, ...VARIANCE_COLUMN_STYLE }}>
            {renderAmountWithQuantity(
              formatCurrency(details.expectedSalesValue ?? 0),
              Number(details.expectedQuantityValue ?? 0).toLocaleString()
            )}
          </TableCell>
        );
      case 'scratcherSalesCombined':
        return (
          <TableCell key={col.columnIndex} className={className} style={{ ...style, ...VARIANCE_COLUMN_STYLE }}>
            {renderAmountWithQuantity(
              formatCurrency(details.posScratcherSalesValue ?? 0),
              Number(details.posScratcherQuantityValue ?? 0).toLocaleString()
            )}
          </TableCell>
        );
      case 'scratcherVarianceCombined':
        return (
          <TableCell key={col.columnIndex} className={withVarianceDollarRightBorder(withVarianceCellBackground(className))} style={{ ...style, ...VARIANCE_COLUMN_STYLE }}>
            {renderAmountWithQuantity(
              <ScratcherVarianceDisplay
                value={details.scratcherRevenueVariance}
                formatValue={formatCurrency}
                title="Scratcher dollar variance"
              />,
              formatSignedTicketVariance(Number(details.scratcherQuantityVariance ?? 0)),
              'tickets',
              Number(details.scratcherRevenueVariance ?? 0) > 0
                ? 'audit-table__amount-with-quantity--positive'
                : Number(details.scratcherRevenueVariance ?? 0) < 0
                  ? 'audit-table__amount-with-quantity--negative'
                  : 'audit-table__amount-with-quantity--center'
            )}
          </TableCell>
        );
      case 'expectedSales':
        return (
          <TableCell key={col.columnIndex} className={className} style={{ ...style, ...VARIANCE_COLUMN_STYLE }}>
            {formatCurrency(details.expectedSalesValue ?? 0)}
          </TableCell>
        );
      case 'scratcherVarianceQty':
        return (
          <TableCell key={col.columnIndex} className={withVarianceCellBackground(className)} style={{ ...style, ...VARIANCE_COLUMN_STYLE }}>
            <ScratcherVarianceDisplay
              value={details.scratcherQuantityVariance}
              formatValue={(value) => value.toLocaleString()}
              title="Scratcher quantity variance"
            />
          </TableCell>
        );
      case 'scratcherVarianceRevenue':
        return (
          <TableCell key={col.columnIndex} className={withVarianceDollarRightBorder(withVarianceCellBackground(className))} style={{ ...style, ...VARIANCE_COLUMN_STYLE }}>
            <ScratcherVarianceDisplay
              value={details.scratcherRevenueVariance}
              formatValue={formatCurrency}
              title="Scratcher dollar variance"
            />
          </TableCell>
        );
      case 'lottoPayouts':
        return (
          <TableCell key={col.columnIndex} className={className} style={{ ...style, ...VARIANCE_COLUMN_STYLE }}>
            {formatCurrency(details.lottoPayoutsValue ?? 0)}
          </TableCell>
        );
      case 'freeTicketPayouts':
      case 'posPayouts':
        return (
          <TableCell key={col.columnIndex} className={className} style={{ ...style, ...VARIANCE_COLUMN_STYLE }}>
            {formatCurrency((col.type === 'freeTicketPayouts' ? details.freeTicketPayoutsValue : details.posPayoutsValue) ?? 0)}
          </TableCell>
        );
      case 'payoutVariance':
        return (
          <TableCell key={col.columnIndex} className={withVarianceDollarRightBorder(withVarianceCellBackground(className))} style={{ ...style, ...VARIANCE_COLUMN_STYLE }}>
            <ScratcherVarianceDisplay
              value={details.payoutVariance}
              formatValue={formatCurrency}
              title="Payout variance"
            />
          </TableCell>
        );
      case 'scratcherFinal':
        // For child rows, show total_variance if available, otherwise show "-"
        const childTotalVariance = item?.reconciliation_variance?.scratcher?.revenue_variance;
        return (
          <TableCell key={col.columnIndex} className={className} style={{ ...style, ...VARIANCE_COLUMN_STYLE }}>
            {childTotalVariance !== undefined && childTotalVariance !== null ? (
              <TotalDiscrepancyDisplay totalDiscrepancy={childTotalVariance} />
            ) : (
              <span style={{ color: '#666' }}>-</span>
            )}
          </TableCell>
        );
      default:
        return null;
    }
  });
};

/**
 * Conditional columns component for child rows
 */
export const ChildRowConditionalColumns = ({
  item,
  isBothConnected,
  isBothNotConnected,
  isLottoConnectedOnly,
  isPOSConnectedOnly,
  borderColumns,
  hasPosType,
  columnOffset = 0,
  hideLottoAndPayout = false,
  rowIdx,
  totalRows
}: {
  item: AuditItem;
  isBothConnected: boolean;
  isBothNotConnected: boolean;
  isLottoConnectedOnly: boolean;
  isPOSConnectedOnly: boolean;
  borderColumns: any;
  hasPosType: boolean;
  columnOffset?: number;
  hideLottoAndPayout?: boolean;
  rowIdx: number;
  totalRows: number;
}) => {
  const values = extractChildRowValues(item);

  const scratcherCountsDetails = prepareScratcherCountsDetails(
    values.posScratcherQuantity,
    values.totalSold,
    item?.reconciliation_variance?.scratcher?.quantity_variance,
    values.isPosZero,
    values.isLottoZero,
    hasPosType
  );

  const scratcherSalesDetails = prepareScratcherSalesDetails(
    values.posScratcherSales,
    values.revenue,
    item?.reconciliation_variance?.scratcher?.revenue_variance,
    values.isPosZero,
    values.isLottoZero,
    hasPosType
  );

  const totalDiscrepancy = item?.reconciliation_variance?.total_variance ?? 0;
  const scratcherQuantityVariance = item?.reconciliation_variance?.scratcher?.quantity_variance ?? 0;
  const scratcherRevenueVariance = item?.reconciliation_variance?.scratcher?.revenue_variance ?? 0;
  const lottoSalesVariance = item?.reconciliation_variance?.lotto_sales_variance ?? 0;
  const payoutVariance = item?.reconciliation_variance?.payout_variance ?? 0;
  const dayLevelConnectedColumns: ColumnType[] = [
    'lottoSystemSales',
    'posLottoSales',
    'lottoSalesVariance',
    'lottoPayouts',
    'freeTicketPayouts',
    'posPayouts',
    'payoutVariance',
  ];
  const dayLevelLottoColumns: ColumnType[] = [
    'lottoSystemSales',
    'lottoPayouts',
  ];

  // Lotto and payout summaries are day-level values, so expanded audit rows leave those columns blank.
  if (isBothConnected) {
    const config = offsetColumnConfig(COLUMN_CONFIGS.bothConnected, columnOffset);
    return <>{renderChildRowColumns(hideLottoAndPayout ? hideLottoAndPayoutColumns(config) : config, {
      scratcherCountsDetails: { simpleValue: values.posScratcherQuantity },
      scratcherSalesDetails: { simpleValue: values.posScratcherSales },
      scratcherQuantityVariance,
      scratcherRevenueVariance,
      expectedSalesValue: values.revenue,
      expectedQuantityValue: values.totalSold,
      posScratcherSalesValue: values.posScratcherSales,
      posScratcherQuantityValue: values.posScratcherQuantity,
      lottoSystemSalesValue: values.lottoSystemSales,
      posLottoSalesValue: values.posLottoSales,
      lottoSalesVariance,
      lottoPayoutsValue: values.lottoPayoutsTotal,
      freeTicketPayoutsValue: values.freeTicketsPaid,
      posPayoutsValue: values.posPayout,
      payoutVariance,
      totalDiscrepancy
    }, item, borderColumns, hasPosType, false, false, rowIdx, totalRows, dayLevelConnectedColumns)}</>;
  }

  if (isBothNotConnected) {
    const config = offsetColumnConfig(COLUMN_CONFIGS.bothNotConnected, columnOffset);
    return <>{renderChildRowColumns(hideLottoAndPayout ? hideLottoAndPayoutColumns(config) : config, {
      scratcherCountsDetails,
      scratcherSalesDetails,
      totalDiscrepancy
    }, item, borderColumns, hasPosType, false, false, rowIdx, totalRows)}</>;
  }

  if (isLottoConnectedOnly) {
    const config = offsetColumnConfig(COLUMN_CONFIGS.singleConnected, columnOffset);
    return <>{renderChildRowColumns(hideLottoAndPayout ? hideLottoAndPayoutColumns(config) : config, {
      scratcherCountsDetails,
      scratcherSalesDetails,
      lottoSystemSalesValue: values.lottoSystemSales,
      lottoPayoutsValue: values.lottoPayoutsTotal,
      totalDiscrepancy
    }, item, borderColumns, hasPosType, false, true, rowIdx, totalRows, dayLevelLottoColumns)}</>;
  }

  if (isPOSConnectedOnly) {
    const config = offsetColumnConfig(COLUMN_CONFIGS.posConnectedOnly, columnOffset);
    return <>{renderChildRowColumns(hideLottoAndPayout ? hideLottoAndPayoutColumns(config) : config, {
      scratcherCountsDetails: { simpleValue: values.posScratcherQuantity },
      scratcherSalesDetails: { simpleValue: values.posScratcherSales },
      scratcherQuantityVariance,
      scratcherRevenueVariance,
      expectedSalesValue: values.revenue,
      expectedQuantityValue: values.totalSold,
      posScratcherSalesValue: values.posScratcherSales,
      posScratcherQuantityValue: values.posScratcherQuantity,
      lottoSystemSalesValue: values.lottoSystemSales,
      posLottoSalesValue: values.posLottoSales,
      lottoSalesVariance,
      lottoPayoutsValue: values.lottoPayoutsTotal,
      freeTicketPayoutsValue: values.freeTicketsPaid,
      posPayoutsValue: values.posPayout,
      payoutVariance,
      totalDiscrepancy
    }, item, borderColumns, hasPosType, true, false, rowIdx, totalRows, dayLevelConnectedColumns)}</>;
  }

  return null;
};

/**
 * Utility function to get store configuration flags
 */
export const getStoreConfigurationFlags = (selectedStore: any) => {
  const isBothConnected = selectedStore?.pos_type && selectedStore?.pos_type !== "n/a" && selectedStore?.lotto_authorized;
  const isBothNotConnected = (!selectedStore?.pos_type || selectedStore?.pos_type === "n/a") && (!selectedStore?.lotto_authorized);
  const isLottoConnectedOnly = selectedStore?.lotto_authorized && (!selectedStore?.pos_type || selectedStore?.pos_type === "n/a");
  const isPOSConnectedOnly = selectedStore?.pos_type && selectedStore?.pos_type !== "n/a" && !selectedStore?.lotto_authorized;

  return {
    isBothConnected,
    isBothNotConnected,
    isLottoConnectedOnly,
    isPOSConnectedOnly,
    isLottoAuthorized: !!selectedStore?.lotto_authorized,
    hasPosType: selectedStore?.pos_type && selectedStore?.pos_type !== "n/a"
  };
};

/**
 * Utility function to create reconciliation display
 */
export const createReconciliationDisplay = (
  totalDiscrepancy: any,
  reconciliation: any,
  handleReconcile: (e: React.MouseEvent) => void,
  isBothConnected: boolean,
  hasPosType: boolean = true,
  verified?: { is_verified?: boolean; date_time?: string; user_id?: string; user_name?: string; note?: string } | null
) => {
  if (typeof totalDiscrepancy === 'object' && 'isNA' in totalDiscrepancy) {
    return (
      <span style={{ color: '#666' }} title="No data available">-</span>
    );
  }

  // 1.) when row?.verified this that time show the checkmark like currently show and next line show by user name .
  if (verified?.is_verified) {
    const verifiedDate = verified?.date_time ? new Date(verified.date_time).toLocaleString() : '-';
    const verifiedBy = verified?.user_name ?? '-';
    const verifiedTitle = verifiedDate || '-';

    return (
      <Tooltip title={verifiedTitle} arrow>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            gap: '2px',
            padding: '4px 0'
          }}
          onClick={handleReconcile}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 1 }}>
            <img src={reviewedIcon} alt="verified" style={{ width: '24px', height: '24px' }} />
          </div>
          <span
            style={{
              fontSize: '0.75rem',
              color: '#666',
              textAlign: 'center',
              lineHeight: '1.2',
              fontFamily: 'inherit',
              opacity: 1
            }}
          >
            by {verifiedBy}
          </span>
        </div>
      </Tooltip>
    );
  }

  const discrepancyClass = totalDiscrepancy > 0
    ? 'reconciliation--discrepancy--positive'
    : 'reconciliation--discrepancy--negative';

  // 2.) for unverified rows, always show "See Details"
  return (
    <span
      className={discrepancyClass}
      onClick={handleReconcile}
      title="Click to reconcile this discrepancy"
      style={{ display: 'block', textAlign: 'center' }}
    >
      See Details
    </span>
  );
};
